import * as _ from 'lodash';
import { createSelector } from 'reselect';

import type { BuildWithTask, DependencyQueryWithFilters } from '../../../shared/dependenciesV2';
import type { DependencyFilters } from '../../../shared/dependenciesV2/filterSchema';
import type { AnnotatedLicense } from '../../types';
import type { ReduxStore } from '../store';

import initialState from './initialState';
import type { DependenciesV2Store } from './types';

export const getDependenciesV2Store = (state: ReduxStore) => state.dependenciesV2;
const createDependenciesV2Selector = <R>(selectorFn: (state: DependenciesV2Store) => R) =>
  createSelector(getDependenciesV2Store, selectorFn);

/**
 * Get the current dependency scope
 */
export const getScope = createDependenciesV2Selector((state: DependenciesV2Store) => state.scope);

/**
 * Get whether the current user belongs to the current project's org.
 */
export const getIsOrgMember = createDependenciesV2Selector((state: DependenciesV2Store) => state.isOrgMember);

/**
 * Helper for checking for project scope (used for conditional rendering)
 */
export const getIsProjectScope = createSelector(getScope, (scope) => scope !== null && scope.scope === 'project');

/**
 * Helper for checking for release group scope (used for conditional rendering)
 */
export const getIsReleaseGroupScope = createSelector(getScope, (scope) => scope !== null && scope.scope === 'releaseGroup');

/**
 * Provides the current page
 */
export const getPage = createDependenciesV2Selector((state: DependenciesV2Store) => state.page);

/**
 * Provides the page count
 */
export const getCount = createDependenciesV2Selector((state: DependenciesV2Store) => state.count);

/**
 * Get all filters
 */
export const getFilters = createDependenciesV2Selector((state: DependenciesV2Store) => state.filters);

/**
 * Get the dependency manager version
 */
export const getDepManagerVersion = createDependenciesV2Selector(
  (state: DependenciesV2Store) => state.depManagerVersion,
);

export const getAreFiltersDefault = createSelector(getFilters, filters => {
  const {
    filters: initialFilters,
  } = initialState;

  return _.isEqual(filters, initialFilters);
});
/**
 * Get all data necessary to build the fetch query
 */
export const getFetchDependenciesQuery = createSelector([getPage, getCount, getFilters, getDepManagerVersion], (
  page: number,
  count: number,
  filters: DependencyFilters,
  depManagerVersion: DependenciesV2Store['depManagerVersion'],
): DependencyQueryWithFilters => ({
  page,
  count,
  ...(depManagerVersion ? { v: depManagerVersion } : {}),
  ...filters,
}));

/**
 * Get the total number of dependencies
 */
export const getTotalDependencies = createDependenciesV2Selector(
  (state: DependenciesV2Store) => state.totalDependencies,
);

const getDependenciesState = createDependenciesV2Selector((state: DependenciesV2Store) => state.dependencies);
/**
 * Get dependencies
 */
export const getDependencies = createSelector(getDependenciesState, state => state.value?.response);

/**
 * Get dependency info for a specific dep
 * @param {string} locator to get
 */
export const getDependency = (locator: string) => createSelector(
  getDependencies,
  (dependencies) => dependencies?.find(dependency => dependency.locator === locator),
);

/**
 * Get if dependencies are loading
 */
export const getAreDependenciesLoading = createSelector(getDependenciesState, state => state.isLoading);

/**
 * Get if there are errors loading dependencies
 */
export const getFetchDependenciesErrors = createSelector(getDependenciesState, state => state.errors);

const getLicenseState = createDependenciesV2Selector((state: DependenciesV2Store) => state.licenses);
/**
 * Get full license response
 */
const getLicenseAnnotations = createSelector(getLicenseState, state => state.value);
/**
 * Get list of unique licenses on the project
 */
export const getLicenses = createSelector(
  getLicenseAnnotations,
  (licenses) => _.uniq(
    licenses?.map((license: AnnotatedLicense) => license.licenseId)
      .sort((a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase())),
  ),
);
/**
 * Get if licenses are loading
 */
export const getAreLicenseAnnotationsLoading = createSelector(getLicenseState, state => state.isLoading);

/**
 * Get internal packageManagers state (includes isLoading and data)
 */
const getPackageManagersState = createDependenciesV2Selector((state: DependenciesV2Store) => state.packageManagers);

/**
 * Get package managers
 */
export const getPackageManagers = createSelector(getPackageManagersState, state => state.value ?? []);

/**
 * Get if package managers are loading
 */
export const getArePackageManagersLoading = createSelector(getPackageManagersState, state => state.isLoading);

/**
 * Get internal rootProjects state (includes isLoading and data)
 */
const getRootProjectsState = createDependenciesV2Selector((state: DependenciesV2Store) => state.rootProjects);

/**
 * Get root projects (sorted by title)
 */
export const getRootProjects = createSelector(
  getRootProjectsState,
  state => _.sortBy(state.value ?? [], (project) => project.title),
);

/**
 * Get if root projects are loading
 */
export const getAreRootProjectsLoading = createSelector(getRootProjectsState, state => state.isLoading);

const getBuildWatchState = createDependenciesV2Selector((state: DependenciesV2Store) => state.buildWatch);

/**
 * Get build watch interval ID
 */
export const getBuildWatchInterval = createSelector(getBuildWatchState, state => state.interval);

/**
 * Get revisions being watched
 */
export const getWatchedRevisions = createSelector(getBuildWatchState, state => state.revisionsToWatch);

/**
 * Get all revision data from build watch
 */
export const getBuildWatchRevisions = createSelector(getBuildWatchState, state => state.revisions.value);
/**
 * Get revision data for a specific revision
 * @param {string} revision to get data for
 */
export const getBuildWatchRevision = (revision: string) =>
  createSelector(getBuildWatchRevisions, (revisions) => revisions?.[revision]);

// Sorting utility
const sortBuildsByDate = (buildA: BuildWithTask, buildB: BuildWithTask) => {
  const startedA = new Date(buildA.task?.started || 0).getTime();
  const startedB = new Date(buildB.task?.started || 0).getTime();

  return startedA - startedB;
};

/**
 * Gets the latest build (by updatedAt) for a revision
 */
export const getLatestBuildOnRevision = (revision: string) =>
  createSelector(getBuildWatchRevisions, state => {
    const builds = state?.[revision]?.builds;
    if (!builds?.length) {
      return null;
    }

    return builds.sort(sortBuildsByDate)[0];
  });
